import React from "react";
import { Table, Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
    FaLink,
    FaEllipsisV,
    FaExclamationCircle,
    FaExternalLinkAlt,
} from "react-icons/fa";
import DomainStatusBadge from "./DomainStatusBadge";
import BasicButton from "../../Buttons/BasicButton";
import { CustomDomainListData, DomainStatus } from "../types";

interface ConnectedDomainsTableProps {
    domains: CustomDomainListData[];
    onVerify?: (domain: CustomDomainListData) => void;
    onCompleteSetup?: (domain: CustomDomainListData) => void;
    onRetry?: (domain: CustomDomainListData) => void;
    onRemove?: (domain: CustomDomainListData) => void;
}

interface KebabToggleProps {
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const KebabToggle = React.forwardRef<HTMLButtonElement, KebabToggleProps>(
    ({ onClick }, ref) => (
        <button
            ref={ref}
            type="button"
            className="btn btn-sm btn-light border-0 bg-transparent"
            onClick={(event) => {
                event.preventDefault();
                onClick?.(event);
            }}
        >
            <FaEllipsisV />
        </button>
    ),
);
KebabToggle.displayName = "KebabToggle";

const formatDate = (isoDate: string) =>
    new Date(isoDate).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });

const ConnectedDomainsTable: React.FC<ConnectedDomainsTableProps> = ({
    domains,
    onVerify,
    onCompleteSetup,
    onRetry,
    onRemove,
}) => {
    const { t } = useTranslation();

    const getActionDropdownItems = (domain: CustomDomainListData) => {
        const items: {
            key: string;
            label: string;
            onClick: () => void;
            className?: string;
        }[] = [];

        if (domain.status === DomainStatus.VERIFICATION_REQUIRED) {
            items.push({
                key: "verify",
                label: t("customDomains.verify"),
                onClick: () => onVerify?.(domain),
            });
        }

        if (
            domain.status !== DomainStatus.REMOVING &&
            domain.status !== DomainStatus.REMOVED
        ) {
            items.push({
                key: "remove",
                label: t("customDomains.remove"),
                onClick: () => onRemove?.(domain),
                className: "text-danger",
            });
        }

        return items;
    };

    const activeDomains = domains.filter((value) => value.status === DomainStatus.ACTIVE);

    return (
        <div className="border rounded-3 mb-4">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 p-3 border-bottom">
                <div className="d-flex align-items-center gap-2">
                    <FaLink className="text-muted" />
                    <span className="fw-semibold">
                        {t("customDomains.connectedDomains")}
                    </span>
                    <span className="badge bg-secondary-subtle text-secondary-emphasis fw-normal">
                        {
                            activeDomains.length
                        }{" "}
                        {t("customDomains.activeDomains")}
                    </span>
                </div>
            </div>

            <div className="table-responsive">
                <Table hover responsive className="align-middle mb-0">
                    <thead>
                        <tr>
                            <th className="text-muted small text-uppercase ps-4">
                                {t("customDomains.domain")}
                            </th>
                            <th className="text-muted small text-uppercase">
                                {t("customDomains.status")}
                            </th>
                            <th className="text-muted small text-uppercase">
                                {t("customDomains.addedDate")}
                            </th>
                            <th className="text-muted small text-uppercase text-end pe-4">
                                {t("customDomains.actions")}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {domains.map((domain) => {
                            const actionDropdownItems =
                                getActionDropdownItems(domain);

                            return (
                                <tr key={domain.id}>
                                    <td className="ps-4">
                                        <div className="d-flex align-items-center gap-2">
                                            <span>{domain.hostname}</span>
                                            {domain.status ===
                                                DomainStatus.ACTIVE && (
                                                <a
                                                    href={`https://${domain.hostname}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-decoration-none d-inline-flex align-items-center gap-1"
                                                    title={t("customDomains.visitDomain")}
                                                >
                                                    <FaExternalLinkAlt
                                                        size={12}
                                                    />
                                                </a>
                                            )}
                                        </div>
                                        {domain.status ===
                                            DomainStatus.VERIFICATION_REQUIRED && (
                                            <button
                                                type="button"
                                                className="btn btn-link btn-sm p-0 text-decoration-none"
                                                onClick={() =>
                                                    onCompleteSetup?.(domain)
                                                }
                                            >
                                                {t(
                                                    "customDomains.completeSetup",
                                                )}{" "}
                                                &rarr;
                                            </button>
                                        )}
                                        {domain.status ===
                                            DomainStatus.FAILED && (
                                            <div className="text-danger small d-flex align-items-center gap-1">
                                                <FaExclamationCircle
                                                    size={12}
                                                />
                                                {t(
                                                    "customDomains.verificationFailed",
                                                )}
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        <DomainStatusBadge
                                            status={domain.status}
                                            createdAt={domain.created_at}
                                        />
                                    </td>
                                    <td className="text-muted">
                                        {t("customDomains.added")}{" "}
                                        {formatDate(domain.created_at)}
                                    </td>
                                    <td className="pe-4">
                                        <div className="d-flex align-items-center justify-content-end gap-2">
                                            {domain.status ===
                                                DomainStatus.FAILED && (
                                                <BasicButton
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() =>
                                                        onRetry?.(domain)
                                                    }
                                                >
                                                    {t(
                                                        "customDomains.retryDns",
                                                    )}
                                                </BasicButton>
                                            )}
                                            {actionDropdownItems.length > 0 && (
                                                <Dropdown align="end">
                                                    <Dropdown.Toggle
                                                        as={KebabToggle}
                                                    />
                                                    <Dropdown.Menu
                                                        popperConfig={{
                                                            strategy: "fixed",
                                                        }}
                                                        renderOnMount
                                                    >
                                                        {actionDropdownItems.map(
                                                            ({
                                                                key,
                                                                label,
                                                                onClick,
                                                                className,
                                                            }) => (
                                                                <Dropdown.Item
                                                                    key={key}
                                                                    className={
                                                                        className
                                                                    }
                                                                    onClick={
                                                                        onClick
                                                                    }
                                                                >
                                                                    {label}
                                                                </Dropdown.Item>
                                                            ),
                                                        )}
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </div>
        </div>
    );
};

export default ConnectedDomainsTable;
